import { useState, useEffect } from "react";
import type { Placement } from "@/types";
import {
  createPlacementRequestSchema,
  updatePlacementRequestSchema,
} from "@/validation";
import { zodToFieldErrors, firstFieldErrors } from "@trillion/validation";
import { Button } from "@trillion/ui/components/ui/button";
import { Input } from "@trillion/ui/components/ui/input";
import { Label } from "@trillion/ui/components/ui/label";
import { Switch } from "@trillion/ui/components/ui/switch";
import { Textarea } from "@trillion/ui/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@trillion/ui/components/ui/card";
import { Loader2 } from "lucide-react";

interface PlacementFormData {
  zone: string;
  slot: string;
  label: string;
  description: string;
  active: boolean;
}

interface PlacementFormProps {
  initialData?: Placement;
  onSubmit: (data: Partial<Placement>) => Promise<void>;
  isLoading: boolean;
}

function toFormData(placement?: Placement): PlacementFormData {
  if (!placement) {
    return { zone: "", slot: "", label: "", description: "", active: true };
  }
  return {
    zone: placement.zone,
    slot: String(placement.slot),
    label: placement.label,
    description: placement.description ?? "",
    active: placement.active,
  };
}

export function PlacementForm({ initialData, onSubmit, isLoading }: PlacementFormProps) {
  const [form, setForm] = useState<PlacementFormData>(() => toFormData(initialData));
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setForm(toFormData(initialData));
    }
  }, [initialData]);

  function updateField(field: keyof PlacementFormData, value: string | boolean) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    // slot is a whole number on the wire (PG INT / C# int). Pre-check for a friendly message; the
    // schema then enforces the [0, 2147483647] range.
    const slotRaw = form.slot.trim();
    if (slotRaw === "" || !/^\d+$/.test(slotRaw)) {
      setErrors((prev) => ({ ...prev, slot: "Slot must be a whole number (0 or greater)" }));
      return;
    }

    // description follows the null-guarded update convention: empty → null (create: no description;
    // edit: "unchanged"). Clearing a previously-set description is not supported by this mirror.
    const description = form.description.trim() === "" ? null : form.description.trim();

    const payload = {
      zone: form.zone.trim(),
      slot: Number(slotRaw),
      label: form.label.trim(),
      description,
      active: form.active,
    };
    const schema = initialData
      ? updatePlacementRequestSchema
      : createPlacementRequestSchema;
    const result = schema.safeParse(payload);
    if (!result.success) {
      setErrors(firstFieldErrors(zodToFieldErrors(result.error)));
      return;
    }

    await onSubmit(payload);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{initialData ? "Edit Placement" : "New Placement"}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="zone">Zone *</Label>
              <Input
                id="zone"
                value={form.zone}
                onChange={(e) => updateField("zone", e.target.value)}
                placeholder="sidebar"
              />
              {errors.zone && <p className="text-sm text-destructive">{errors.zone}</p>}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="slot">Slot *</Label>
              <Input
                id="slot"
                type="number"
                min={0}
                value={form.slot}
                onChange={(e) => updateField("slot", e.target.value)}
                placeholder="1"
              />
              {errors.slot && <p className="text-sm text-destructive">{errors.slot}</p>}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="label">Label *</Label>
            <Input
              id="label"
              value={form.label}
              onChange={(e) => updateField("label", e.target.value)}
              placeholder="Sidebar — Top"
            />
            {errors.label && <p className="text-sm text-destructive">{errors.label}</p>}
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              className="min-h-[80px]"
              value={form.description}
              onChange={(e) => updateField("description", e.target.value)}
              placeholder="What this placement is for (optional)"
            />
            {errors.description && (
              <p className="text-sm text-destructive">{errors.description}</p>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Switch
              id="active"
              checked={form.active}
              onCheckedChange={(checked) => updateField("active", checked)}
            />
            <Label htmlFor="active">Active</Label>
          </div>

          <div className="flex gap-3">
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 size-4 animate-spin" />}
              {initialData ? "Update Placement" : "Create Placement"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
